import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Card,
  Icon,
  Text,
  Separator,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverPopup,
  useIsMobile,
} from "@salestrak/ui";
import type { IssueRow } from "#app/lib/data";
import { AgeLabel } from "#app/components/age/age-label";
import { AgeHistoryPopover } from "#app/components/age/age-history-popover";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { EntityTag, useEntityDetail } from "#app/components/entity";
import { useIssueView } from "#app/components/issue-view";
import { useNotesPreview } from "#app/hooks/issue-view/use-notes-preview";
import { stripHtmlToText } from "@salestrak/utilities";
import { formatNoteDate } from "#app/lib/utils/format-date";
import { IssueCardAssigneeSelector } from "./issue-card-assignee-selector";
import { IssueMenu } from "#app/components/issue-actions/issue-menu";
import { canEdit } from "#app/lib/utils/access-level";
import type { Omittable } from "@salestrak/ui";

// The hover previews are edge-to-edge lists; the stock popover viewport insets
// its content, so the padding is zeroed at the viewport slot.
const PREVIEW_POPUP =
  "w-[300px]";

type IssueCardProps = Omittable<{
  issue: IssueRow;
  isDragOverlay?: boolean;
  /** Bulk-selection state, from the board's `renderCard` meta. */
  isSelected?: boolean;
}>;

export const IssueCard = React.memo(function IssueCard({
  issue,
  isDragOverlay,
  isSelected,
}: IssueCardProps) {
  const { openIssue } = useIssueView();
  const { openEntity } = useEntityDetail();
  const isMobile = useIsMobile();
  const [ageDialogOpen, setAgeDialogOpen] = useState(false);
  // Stop the click from bubbling to the KanbanCard's own onClick, which would
  // otherwise also fire openIssue() (defaulting to the summary tab) behind the
  // age dialog.
  const handleAgeClick = useCallback((e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setAgeDialogOpen(true);
  }, []);
  const handleViewProfile = useCallback(
    (userId: string) => openEntity("user", userId),
    [openEntity],
  );
  // Editing the card (read-only context menu) is gated on access. The drag
  // affordance lives on the library KanbanCard wrapper, which reads
  // `isItemDragDisabled` from the board.
  const dragDisabled = !canEdit(issue.viewerAccessLevel);

  // Hover previews are desktop-only. On mobile (touch) there is no hover, and
  // wrapping the button in a Popover would make a tap both toggle the popover
  // and fire the click — so on mobile we render the bare buttons and let their
  // own handlers (open the age dialog / open the activity tab) run.
  const hasAge = !!issue.age?.status && issue.age.status !== "disabled";

  const ageButton = (
    <Button variant="ghost" size="xs" onClick={handleAgeClick}>
      <AgeLabel age={issue.age} size="sm" />
    </Button>
  );

  const noteButton = (
    <Button
      variant="ghost"
      size="xs"
      onClick={(e) => {
        // Stop the click bubbling to the KanbanCard's onClick, which fires
        // openIssue() with no options and would reset the tab to summary.
        e.stopPropagation();
        openIssue(issue, { initialTab: "activity" });
      }}
    >
      <Icon
        name={issue.hasUnreadNote ? "message-square-dot" : "message-square"}
        size="md"
        color={issue.hasUnreadNote ? undefined : "muted"}
      />
      <Text
        size="sm"
        variant={issue.hasUnreadNote ? "default" : "muted"}
        weight={issue.hasUnreadNote ? "bold" : undefined}
      >
        {issue.noteCount}
      </Text>
    </Button>
  );

  const cardContent = (
    <Card
      data-testid={`issue-card-${issue.issueId}`}
      // One step above the column's card-list area, so the stack reads as
      // cards resting in a well rather than panels flush with it. Hover is a
      // wash over that level, not a deeper shadow.
      surface="card"
      hoverBackground
      selected={isSelected}
      minWidth={0}
      overflow="hidden"
      radius="sm"
      shrink
    >
      {/* One padding layer. Two — an outer 8px plus an inner 4px on the header
          only — is what left the name indented 12px while the footer row below
          it sat at 8px. */}
      <Box direction="column" padding="base">
        {/* Header */}
        <Box direction="column">
          <Text size="sm" weight="semibold" truncate={2}>
            {issue.name}
          </Text>
          {issue.address?.wholeAddress && (
            <Text variant="muted" size="xs" weight="medium">
              {issue.address.wholeAddress}
            </Text>
          )}
        </Box>

        <Box direction="column" paddingTop="base">
          <Separator />
        </Box>

        {/* Footer */}
        <Box
          direction="row"
          align="center"
          justify="between"
          paddingTop="sm"
          gap="sm"
        >
          <Box direction="row" align="center" gap="xs" shrink>
            {hasAge ? (
              <AgeHistoryPopover issue={issue}>{ageButton}</AgeHistoryPopover>
            ) : (
              <AgeLabel
                age={issue.age}
                size="sm"
                buttonSize="xs"
                buttonVariant="ghost"
                onClick={handleAgeClick}
              />
            )}
            {issue.noteCount > 0 &&
              (isMobile ? (
                noteButton
              ) : (
                <Popover>
                  <PopoverTrigger openOnHover render={noteButton} />
                  <PopoverPopup side="top" padding="none" className={PREVIEW_POPUP}>
                    <IssueNotesPreview issue={issue} />
                  </PopoverPopup>
                </Popover>
              ))}
          </Box>

          {/* Assignee selector */}
          <Box direction="column" shrink={false} align="center">
            <IssueCardAssigneeSelector
              issueId={issue.issueId}
              assignees={issue.assignees ?? []}
              onViewProfile={handleViewProfile}
              viewerAccessLevel={issue.viewerAccessLevel}
            />
          </Box>
        </Box>
      </Box>
    </Card>
  );

  return (
    <>
      {isDragOverlay ? (
        cardContent
      ) : (
        <IssueMenu issue={issue} type="context" readOnly={dragDisabled}>
          {cardContent}
        </IssueMenu>
      )}
      <UpdateAgeDialog
        open={ageDialogOpen}
        onOpenChange={setAgeDialogOpen}
        issueId={issue.issueId}
        stageId={issue.stageId}
        age={issue.age}
      />
    </>
  );
});

/**
 * Hover preview for the card's notes button. Backed by `useNotesPreview`,
 * which owns its own slim query (separate from the kanban board read and the
 * activity view) and orders rows so unread notes always appear first and are
 * never dropped. Mounts only when the popover opens, so the query is active
 * for just the one hovered card.
 */
function IssueNotesPreview({ issue }: { issue: IssueRow }) {
  const { openIssue } = useIssueView();
  const { notes, loading } = useNotesPreview(issue.issueId);

  if (loading && notes.length === 0) {
    return (
      <Box direction="column" padding="md" align="center">
        <Spinner size="md" />
      </Box>
    );
  }

  if (notes.length === 0) {
    return (
      <Box direction="column" padding="md">
        <Text size="sm" variant="muted">
          No notes yet
        </Text>
      </Box>
    );
  }

  return (
    <Box direction="column">
      {notes.map((note, index) => (
        <Box
          as="button"
          key={`${note.noteId}-${index}`}
          direction="column"
          fill="width"
          onClick={() =>
            openIssue(issue, {
              initialTab: "activity",
              focusNoteId: note.noteId,
            })
          }
          paddingX="md"
          paddingY="sm"
          gap="xs"
          hoverBackground
        >
          <Box direction="row" align="center" justify="between" gap="sm" fill="width">
            <Box direction="row" align="center" gap="xs" shrink>
              <EntityTag
                entity={{
                  kind: "user",
                  id: note.author?.userId ?? "system",
                  user: note.author ?? null,
                }}
                display="full"
                size="sm"
                system={!note.author}
                interactive={false}
              />
            </Box>
            <Text size="xs" variant="muted" truncate>
              {formatNoteDate(note.lastUpdateDate ?? note.creationDate)}
              {note.edited ? " (edited)" : ""}
            </Text>
          </Box>
          <Text
            size="sm"
            weight={note.unread ? "bold" : undefined}
            variant={note.unread ? "default" : "muted"}
            truncate={2}
          >
            {stripHtmlToText(note.note)}
          </Text>
        </Box>
      ))}
    </Box>
  );
}
